import { TimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { isOvernightHours, TimeSpan, dayJsTimeFormat, dayJsTimeFormatDisplay } from "../../../common/BusinessHours";
import Row from "../../../common/Row";
import { capitalizeFirstLetter } from "../../../common/utilities";

type BusinessHoursDayEntryProps = {
  day: string;
  timeSpans: TimeSpan[];
  updateAndValidateHours: (timeSpans: TimeSpan[]) => void;
  errors?: {
    openTimeError?: string;
    closeTimeError?: string;
  }[];
};

const BusinessHoursDayEntry = ({ day, timeSpans, updateAndValidateHours, errors }: BusinessHoursDayEntryProps) => {
  const [isClosed, setIsClosed] = useState<boolean>((!timeSpans[0].openTime && !timeSpans[0].closeTime));
  const [prevTimeSpans, setPrevTimeSpans] = useState<TimeSpan[]>(
    isClosed
      ? [{
        openTime: dayjs("08:00:00", dayJsTimeFormat),
        closeTime: dayjs("17:00:00", dayJsTimeFormat)
      }]
      : timeSpans
  );

  const updateTimeSpans = (timeSpans: TimeSpan[]) => {
    setPrevTimeSpans(timeSpans);
    updateAndValidateHours(timeSpans);
  }

  const addTimeSpan = () => {
    updateTimeSpans([...timeSpans, { openTime: null, closeTime: null }]);
  };

  const removeTimeSpan = (index: number) => {
    if (timeSpans.length > 1) {
      updateTimeSpans(timeSpans.filter((_, i) => i !== index));
    }
  };

  const updateOpenOrCloseTime = (index: number, field: "openTime" | "closeTime", time: Dayjs | null) => {
    const updatedTimeSpans = [...timeSpans];
    updatedTimeSpans[index][field] = time;
    updateTimeSpans(updatedTimeSpans);
  };

  const closeTimeLabel = (timeSpan: TimeSpan) => {
    if (isClosed) {
      return "CLOSED";
    } else if (isOvernightHours(timeSpan.openTime, timeSpan.closeTime)) {
      return "Close Time (next day)";
    } else {
      return "Close Time";
    }
  }

  const toggleClosedCheckbox = (checked: boolean) => {
    updateAndValidateHours(checked ? [] : prevTimeSpans);
    setIsClosed(checked);
  }
  
  return <Row className="pb-3" data-testid={`business-hours-${day}-container`}>
    <div className="col-2">
      {capitalizeFirstLetter(day)}
    </div>
    <div className="col-8">
      {timeSpans.map((span, index) => (
        <Row key={index} className="mb-2">
          <div className="col-5" data-testid={`business-hours-${day}-openTime-${index}`}>
            <TimePicker
              label={isClosed ? "CLOSED" : "Open Time"}
              value={span.openTime}
              onChange={(newValue) => updateOpenOrCloseTime(index, "openTime", newValue)}
              format={dayJsTimeFormatDisplay}
              disabled={isClosed}
              slotProps={{
                textField: {
                  helperText: errors?.[index]?.openTimeError,
                  error: !!errors?.[index]?.openTimeError,
                }
              }}
            />
          </div>
          <div className="col-5" data-testid={`business-hours-${day}-closeTime-${index}`}>
            <TimePicker
              label={closeTimeLabel(span)}
              value={span.closeTime}
              onChange={(newValue) => updateOpenOrCloseTime(index, "closeTime", newValue)}
              format={dayJsTimeFormatDisplay}
              disabled={isClosed}
              slotProps={{
                textField: {
                  helperText: errors?.[index]?.closeTimeError,
                  error: !!errors?.[index]?.closeTimeError,
                }
              }}
              minTime={dayjs("00:00:00", dayJsTimeFormat)}
            />
          </div>
          <div className="col-2">
            {(timeSpans.length !== 1 && index !== timeSpans.length - 1) && (
              <button
                className="btn btn-ghost-dark btn-icon rounded-circle"
                onClick={() => removeTimeSpan(index)}
                data-testid={`business-hours-${day}-remove-${index}`}>
                <i className="bi-dash-circle" style={{ fontSize: 30 }} />
              </button>
            )}
            {(timeSpans.length === 1 || index === timeSpans.length - 1) && (
              <button
                className="btn btn-ghost-dark btn-icon rounded-circle"
                onClick={addTimeSpan}
                data-testid={`business-hours-${day}-add`}>
                <i className="bi-plus-circle" style={{ fontSize: 30 }} />
              </button>
            )}
          </div>
        </Row>
      ))}
    </div>
    <div className="col-2 pt-3">
      <input
        id={`closedCbx${day}`}
        type="checkbox"
        className="form-check-input me-2"
        checked={isClosed}
        onChange={(ev) => toggleClosedCheckbox(ev.target.checked)} />
      <label
        className="form-check-label-lg"
        htmlFor={`closedCbx${day}`}>
        Closed
      </label>
    </div>
  </Row>
}

export default BusinessHoursDayEntry;